/**
 * Pure mappers + partitioner that translate the campaigns API response
 * (`CampaignApiRecord[]` from `GET /gamepass/v2/admin/campaigns`) into the
 * presentational UI model (`SeasonData`) that `SeasonCard` /
 * `MonthlyConfiguration` already consume.
 *
 * Deliberately a Monthly-only counterpart to `seasonsMappers.ts` (rather than
 * a shared module): the campaign lifecycle is a strict subset of the season
 * one (no `locked` / `grace`), and the Monthly landing page buckets records
 * into Active Month / Next Month / Drafts / Completed / Canceled — no
 * standalone "Scheduled" section — so a shared partitioner would need to fork
 * anyway. Centralising every API → UI translation here keeps the page focused
 * on layout and the mapper unit-testable in isolation.
 */

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { AppStrings } from "../../utils/constants/AppStrings";
import { CAMPAIGN_API_STATUS, MONTHLY_FILTER_ID } from "../../types/gamePass";
import type {
  CampaignApiRecord,
  CampaignApiStatus,
  MonthlyFilterId,
  SeasonCountdown,
  SeasonData,
  SeasonMetric,
  SeasonStatus,
  SeasonTrailingKind,
} from "../../types/gamePass";

dayjs.extend(utc);

/** Output of `partitionCampaignsByStatus`. Every bucket is an array — unlike
 *  Seasons (one active season at a time), the campaign backend allows multiple
 *  concurrent active monthly challenges, so `active` collects all of them for
 *  the Active Month section. `next` holds scheduled campaigns (there is no
 *  separate Scheduled section on Monthly); the rest are Drafts / Completed /
 *  Canceled. */
export type PartitionedCampaigns = {
  active: SeasonData[];
  next: SeasonData[];
  drafts: SeasonData[];
  completed: SeasonData[];
  canceled: SeasonData[];
};

/** Map a Monthly filter-tab id onto the *set* of BE statuses that populate the
 *  panel. Timeline needs Active + Scheduled to fill its two columns; the
 *  single-status tabs map 1:1; `All` returns `undefined` (fetch everything and
 *  partition client-side). Mirrors `filterIdToApiStatuses` for seasons. */
export const campaignFilterIdToApiStatuses = (
  filterId: MonthlyFilterId,
): ReadonlyArray<CampaignApiStatus> | undefined => {
  switch (filterId) {
    case MONTHLY_FILTER_ID.TIMELINE:
      return [CAMPAIGN_API_STATUS.ACTIVE, CAMPAIGN_API_STATUS.SCHEDULED];
    case MONTHLY_FILTER_ID.SCHEDULED:
      return [CAMPAIGN_API_STATUS.SCHEDULED];
    case MONTHLY_FILTER_ID.DRAFTS:
      return [CAMPAIGN_API_STATUS.DRAFT];
    case MONTHLY_FILTER_ID.COMPLETED:
      return [CAMPAIGN_API_STATUS.COMPLETED];
    case MONTHLY_FILTER_ID.CANCELED:
      return [CAMPAIGN_API_STATUS.CANCELLED];
    case MONTHLY_FILTER_ID.ALL:
      return undefined;
    default: {
      const _exhaustive: never = filterId;
      return undefined;
    }
  }
};

/** Stable, observable fallback when BE returns a record with neither `id` nor
 *  `campaign_id`. Never returns `""` — an empty id collides across records
 *  (corrupting the React key) and produces a broken `:challengeId=` URL on
 *  navigation. Derives a deterministic key from the record's natural fields
 *  and warns once so the malformed payload is visible in dev / observability. */
const MISSING_CAMPAIGN_ID_PREFIX = "missing-campaign-id";

const buildMissingCampaignIdFallback = (record: CampaignApiRecord): string => {
  const parts = [record.name, record.start_date, record.end_date]
    .filter((part): part is string => Boolean(part))
    .join("|");
  return parts
    ? `${MISSING_CAMPAIGN_ID_PREFIX}:${parts}`
    : MISSING_CAMPAIGN_ID_PREFIX;
};

/** Resolve the campaign id, accepting both `campaign_id` (create response) and
 *  `id` so the consumer survives a BE rename. Falls back to a warned
 *  placeholder rather than an empty string. */
const resolveCampaignId = (record: CampaignApiRecord): string => {
  const id = record.campaign_id ?? record.id;
  if (id) return id;
  // eslint-disable-next-line no-console -- intentional dev signal for a
  // malformed BE payload; cheap to silence via the global logger if noisy.
  console.warn(
    "[campaignsMappers] Campaign record is missing both `campaign_id` and `id`; falling back to a placeholder id.",
    { name: record.name, status: record.status },
  );
  return buildMissingCampaignIdFallback(record);
};

/** Prefer the canonical `name` authored in the wizard; fall back to a
 *  non-empty `display_name` only for malformed payloads where `name` is
 *  blank (matches `resolveSeasonName`). */
const resolveCampaignName = (record: CampaignApiRecord): string => {
  if (typeof record.name === "string" && record.name.trim().length > 0) {
    return record.name;
  }
  const override = record.display_name;
  if (typeof override === "string" && override.trim().length > 0) {
    return override;
  }
  return record.name ?? "";
};

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** Campaign boundaries arrive as date-only strings or UTC timestamps.
 *  Date-only strings have no timezone to convert; timestamps render in the
 *  admin's local wall-clock so cards match the details page. */
const parseCampaignBoundaryDate = (value: string) => {
  const trimmed = value?.trim() ?? "";
  if (DATE_ONLY_PATTERN.test(trimmed)) {
    return dayjs(trimmed, "YYYY-MM-DD", true);
  }
  const zoned = dayjs.utc(trimmed).local();
  return zoned.isValid() ? zoned : dayjs(trimmed);
};

/** Format a [start, end] pair into the card's date-range string
 *  ("May 1 - May 31, 2026"). Returns `""` when neither boundary is present
 *  (a draft may have no real window yet) so the card collapses the row; falls
 *  back to the raw values when they're present but unparseable. */
const formatCampaignDateRange = (
  start: string | null | undefined,
  end: string | null | undefined,
): string => {
  const startRaw = start ?? "";
  const endRaw = end ?? "";
  if (!startRaw && !endRaw) return "";
  const startDay = parseCampaignBoundaryDate(startRaw);
  const endDay = parseCampaignBoundaryDate(endRaw);
  if (!startDay.isValid() || !endDay.isValid()) {
    return `${startRaw} - ${endRaw}`;
  }
  const sameYear = startDay.year() === endDay.year();
  const startFormat = sameYear ? "MMM D" : "MMM D, YYYY";
  return `${startDay.format(startFormat)} - ${endDay.format("MMM D, YYYY")}`;
};

/** Countdown phrase above the metrics: active → "Ends in <N days>" (from
 *  `end_date`); scheduled → "Starts in <N days>" (from `start_date`); drafts /
 *  completed / cancelled have none. Returns `undefined` when the reference
 *  date is missing / unparseable / already elapsed so the row collapses. */
const computeCampaignCountdown = (
  status: CampaignApiStatus,
  start: string | null | undefined,
  end: string | null | undefined,
): SeasonCountdown | undefined => {
  const isActive = status === CAMPAIGN_API_STATUS.ACTIVE;
  const isScheduled = status === CAMPAIGN_API_STATUS.SCHEDULED;
  if (!isActive && !isScheduled) return undefined;
  const reference = isActive ? end : start;
  if (!reference) return undefined;
  const target = dayjs.utc(reference);
  if (!target.isValid()) return undefined;
  const now = dayjs.utc();
  const diffDays = target.startOf("day").diff(now.startOf("day"), "day");
  if (diffDays <= 0) return undefined;
  const dayLabel = diffDays === 1 ? "day" : "days";
  const prefix = isActive
    ? AppStrings.SEASON_COUNTDOWN_ENDS_IN
    : AppStrings.SEASON_COUNTDOWN_STARTS_IN;
  return { prefix, value: `${diffDays} ${dayLabel}` };
};

/** Top-right status pill. Scheduled campaigns render the "Scheduled" pill in
 *  the upNext tone (they live under Next Month); the rest map to their
 *  matching lifecycle tone. */
const buildCampaignStatusBadge = (
  status: CampaignApiStatus,
): SeasonStatus | undefined => {
  switch (status) {
    case CAMPAIGN_API_STATUS.ACTIVE:
      return { label: AppStrings.SEASON_STATUS_ACTIVE, tone: "active" };
    case CAMPAIGN_API_STATUS.SCHEDULED:
      return { label: AppStrings.SEASONS_FILTER_SCHEDULED, tone: "upNext" };
    case CAMPAIGN_API_STATUS.DRAFT:
      return { label: AppStrings.SEASON_STATUS_DRAFT, tone: "draft" };
    case CAMPAIGN_API_STATUS.COMPLETED:
      return { label: AppStrings.SEASON_STATUS_COMPLETED, tone: "completed" };
    case CAMPAIGN_API_STATUS.CANCELLED:
      return { label: AppStrings.SEASON_STATUS_CANCELED, tone: "canceled" };
    default: {
      const _exhaustive: never = status;
      return undefined;
    }
  }
};

/** Bottom-row affordance kind. Every campaign card is navigable (the title
 *  chevron opens the Monthly Details page — wired independently of the
 *  trailing in `buildMonthlyCardProps`); the trailing only controls the extra
 *  bottom-row affordance:
 *    - `"edit"`  → editable statuses (Draft, Scheduled) render the pencil.
 *    - `"locked"`→ Active renders the read-only Locked pill.
 *    - `"none"`  → Completed / Cancelled add no extra affordance (the status
 *                  pill already reads Completed / Canceled), but the card still
 *                  opens its read-only details via the title chevron. */
const resolveCampaignTrailing = (
  status: CampaignApiStatus,
): SeasonTrailingKind => {
  switch (status) {
    case CAMPAIGN_API_STATUS.ACTIVE:
      return "locked";
    case CAMPAIGN_API_STATUS.SCHEDULED:
    case CAMPAIGN_API_STATUS.DRAFT:
      return "edit";
    case CAMPAIGN_API_STATUS.COMPLETED:
    case CAMPAIGN_API_STATUS.CANCELLED:
      return "none";
    default: {
      const _exhaustive: never = status;
      return "none";
    }
  }
};

/** Build the metrics row — only non-undefined counts render, so a payload
 *  that omits a count silently drops that pill rather than showing "0". */
const buildCampaignMetrics = (
  record: CampaignApiRecord,
): ReadonlyArray<SeasonMetric> => {
  const metrics: SeasonMetric[] = [];
  if (typeof record.challenge_count === "number") {
    metrics.push({
      id: "challenges",
      label: AppStrings.SEASON_METRIC_CHALLENGES,
      value: record.challenge_count,
    });
  }
  if (typeof record.task_count === "number") {
    metrics.push({
      id: "tasks",
      label: AppStrings.SEASON_METRIC_TASKS,
      value: record.task_count,
    });
  }
  return metrics;
};

/** Translate a single campaign API record into the presentational `SeasonData`
 *  the card / listing consume. Exported so tests can assert the contract
 *  without mounting the page. */
export const mapApiCampaignToSeasonData = (
  record: CampaignApiRecord,
): SeasonData => ({
  id: resolveCampaignId(record),
  name: resolveCampaignName(record),
  dateRange: formatCampaignDateRange(record.start_date, record.end_date),
  countdown: computeCampaignCountdown(
    record.status,
    record.start_date,
    record.end_date,
  ),
  metrics: buildCampaignMetrics(record),
  status: buildCampaignStatusBadge(record.status),
  trailing: resolveCampaignTrailing(record.status),
});

/** Sort so the most-recently-created record surfaces first (descending
 *  `created_at`). Records missing / with an unparseable `created_at` sink to
 *  the bottom; ties preserve BE order (stable sort). */
const compareByCreatedAtDesc = (
  a: CampaignApiRecord,
  b: CampaignApiRecord,
): number => {
  const toMs = (value?: string | null): number => {
    if (!value) return -Infinity;
    const ms = dayjs.utc(value).valueOf();
    return Number.isFinite(ms) ? ms : -Infinity;
  };
  return toMs(b.created_at) - toMs(a.created_at);
};

/** Drop every campaign into its lifecycle bucket, each sorted by `created_at`
 *  desc (product: "recent on top"). Every bucket accumulates — the campaign
 *  backend permits multiple concurrent active challenges, so `active` collects
 *  all of them rather than picking a single winner. `scheduled` records land
 *  in `next` (Next Month). */
export const partitionCampaignsByStatus = (
  records: ReadonlyArray<CampaignApiRecord>,
): PartitionedCampaigns => {
  // Copy before sorting — `records` may be the React Query cache, which must
  // not be mutated in place.
  const sortedRecords = [...records].sort(compareByCreatedAtDesc);
  const partitioned: PartitionedCampaigns = {
    active: [],
    next: [],
    drafts: [],
    completed: [],
    canceled: [],
  };

  sortedRecords.forEach((record) => {
    const ui = mapApiCampaignToSeasonData(record);
    switch (record.status) {
      case CAMPAIGN_API_STATUS.ACTIVE:
        partitioned.active.push(ui);
        break;
      case CAMPAIGN_API_STATUS.SCHEDULED:
        partitioned.next.push(ui);
        break;
      case CAMPAIGN_API_STATUS.DRAFT:
        partitioned.drafts.push(ui);
        break;
      case CAMPAIGN_API_STATUS.COMPLETED:
        partitioned.completed.push(ui);
        break;
      case CAMPAIGN_API_STATUS.CANCELLED:
        partitioned.canceled.push(ui);
        break;
      default: {
        const _exhaustive: never = record.status;
      }
    }
  });
  return partitioned;
};
